import {FabricObject, Point, util} from 'fabric';

const BASE_GROUP_THRESHOLD = 20;

export type Align = 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom';
export type AlignDirection = 'x' | 'y';

type MeasuredObject = {
    o: FabricObject<any, any, any>;
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
    cx: number;
    cy: number;
    w: number;
    h: number;
};

// Get the object's transformed bounds in canvas space.
const measureObject = (o: FabricObject<any, any, any>): MeasuredObject => {
    o.setCoords();
    const points = o.getCoords();

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    for (const point of points) {
        if (point.x < minX) minX = point.x;
        if (point.y < minY) minY = point.y;
        if (point.x > maxX) maxX = point.x;
        if (point.y > maxY) maxY = point.y;
    }

    const w = maxX - minX;
    const h = maxY - minY;

    return {
        o,
        minX,
        maxX,
        minY,
        maxY,
        w,
        h,
        cx: minX + w / 2,
        cy: minY + h / 2,
    };
};

// Build one bounding box around the full selection.
const measureSelection = (items: MeasuredObject[]) => {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    for (const item of items) {
        if (item.minX < minX) minX = item.minX;
        if (item.minY < minY) minY = item.minY;
        if (item.maxX > maxX) maxX = item.maxX;
        if (item.maxY > maxY) maxY = item.maxY;
    }

    return {
        minX,
        minY,
        maxX,
        maxY,
        cx: (minX + maxX) / 2,
        cy: (minY + maxY) / 2,
    };
};

// Move by canvas delta, converting through group space when needed.
export const moveObjectByCanvasDelta = (
    o: FabricObject<any, any, any>,
    dx: number,
    dy: number
) => {
    if (!dx && !dy) return;

    const centerCanvas = o.getCenterPoint();
    const nextCenterCanvas = new Point(centerCanvas.x + dx, centerCanvas.y + dy);

    if (o.group) {
        // Grouped objects are positioned in local group space, not raw canvas space.
        const inverseGroupMatrix = util.invertTransform(o.group.calcTransformMatrix());
        const nextCenterLocal = util.transformPoint(nextCenterCanvas, inverseGroupMatrix);
        o.setPositionByOrigin(nextCenterLocal, 'center', 'center');
    } else {
        o.setPositionByOrigin(nextCenterCanvas, 'center', 'center');
    }

    o.setCoords();
};

// Compute alignment moves against the current selection bounds.
export const getAlignmentPlan = (
    objects: FabricObject<any, any, any>[],
    alignment: Align
) => {
    const items = objects.map(measureObject);
    const selection = measureSelection(items);

    return items.map((item) => {
        let dx = 0;
        let dy = 0;

        if (alignment === 'left') dx = selection.minX - item.minX;
        else if (alignment === 'center') dx = selection.cx - item.cx;
        else if (alignment === 'right') dx = selection.maxX - item.maxX;
        else if (alignment === 'top') dy = selection.minY - item.minY;
        else if (alignment === 'middle') dy = selection.cy - item.cy;
        else if (alignment === 'bottom') dy = selection.maxY - item.maxY;

        return {object: item.o, dx, dy};
    });
};

// Group items into rows or columns before equal-spacing.
const clusterByCrossAxis = (
    items: MeasuredObject[],
    dir: AlignDirection,
    threshold: number
): MeasuredObject[][] => {
    const crossKey: 'cx' | 'cy' = dir === 'x' ? 'cy' : 'cx';
    const sorted = [...items].sort((a, b) => a[crossKey] - b[crossKey]);
    const groups: MeasuredObject[][] = [];

    for (const item of sorted) {
        const currentGroup = groups[groups.length - 1];
        if (!currentGroup) {
            groups.push([item]);
            continue;
        }

        const groupCenter = currentGroup.reduce((sum, entry) => sum + entry[crossKey], 0) / currentGroup.length;
        if (Math.abs(item[crossKey] - groupCenter) <= threshold) currentGroup.push(item);
        else groups.push([item]);
    }

    return groups;
};

// Precompute spacing moves while preserving row/column structure when possible.
export const getFixedGapPlan = (
    objects: FabricObject<any, any, any>[],
    dir: AlignDirection,
    gapPx: number,
    zoom: number
) => {
    const planned = new Map<FabricObject<any, any, any>, { dx: number; dy: number }>();
    if (objects.length < 2) return planned;

    const items = objects.map(measureObject);
    const threshold = (BASE_GROUP_THRESHOLD * 2) / Math.max(zoom, 0.0001);
    let groups = clusterByCrossAxis(items, dir, threshold);

    // If no row or column was detected, treat the whole selection as one spacing run.
    if (!groups.some((group) => group.length >= 2)) {
        groups = [items];
    }

    for (const group of groups) {
        if (group.length < 2) continue;

        // Horizontal spacing anchors the leftmost item and lays out the rest to the right.
        if (dir === 'x') {
            const sorted = [...group].sort((a, b) => a.minX - b.minX);
            let cursor = sorted[0].minX;

            for (const item of sorted) {
                planned.set(item.o, {dx: cursor - item.minX, dy: 0});
                cursor += item.w + gapPx;
            }
            continue;
        }

        // Vertical spacing anchors the topmost item and lays out the rest downward.
        const sorted = [...group].sort((a, b) => a.minY - b.minY);
        let cursor = sorted[0].minY;

        for (const item of sorted) {
            planned.set(item.o, {dx: 0, dy: cursor - item.minY});
            cursor += item.h + gapPx;
        }
    }

    return planned;
};
